import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Typography from "@material-ui/core/Typography";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { DataGridItem } from "components/data-grid";
import Select from "components/select";
import withStyles from "components/with-styles";
import styles from "./styles/RecordingsSection.css.js";

const RecordingsSection = ({
  intl,
  values,
  allStreamSources,
  selectedStreamSources,
  selectedRecordings,
  handleRecordingSelectionChange,
  handleRecordingAutorunChange,
  handleRecordingFieldChange,
  handleProgramRecordingSelectionChange,
  handleProgramRecordingAutorunChange,
  handleProgramRecordingFieldChange,
  classes,
}) => {
  const recordingAdvancedSettingsAvailable = false;

  return (
    <>
      <DataGridItem big>
        <Typography variant="h6" gutterBottom className={classes.title}>
          {intl.formatMessage({ id: "roomTemplate.recordings.title" }, { defaultMessage: "Recordings" })}
        </Typography>
        <Typography variant="caption" color="textSecondary" className={classes.instructions}>
          {intl.formatMessage(
            { id: "roomTemplate.recordings.instructions" },
            { defaultMessage: "Select stream sources to record and configure recording settings" },
          )}
        </Typography>
        {selectedStreamSources.length > 0 ? (
          selectedStreamSources.map(streamSourceIndex => {
            const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
            const formSource = values.file?.stream_sources?.[selectedIndex];
            const streamSource = allStreamSources[streamSourceIndex];
            const label = formSource?.label || streamSource?.label || "";
            const hasLabel = !!label;
            const isRecordingSelected = selectedRecordings.includes(streamSourceIndex);
            const currentRecordings = values.file?.video_drains?.recordings || [];
            const recording = currentRecordings.find(rec => rec.src === label) || {};

            return (
              <Accordion key={`recording-${streamSourceIndex}`} className={classes.accordion} disabled={!hasLabel}>
                <AccordionSummary
                  expandIcon={recordingAdvancedSettingsAvailable ? <ExpandMoreIcon /> : null}
                  aria-controls={`recording-${streamSourceIndex}-content`}
                  id={`recording-${streamSourceIndex}-header`}
                >
                  <div className={classes.accordionSummaryContainer}>
                    <Checkbox
                      checked={isRecordingSelected}
                      onChange={handleRecordingSelectionChange(streamSourceIndex)}
                      onClick={e => e.stopPropagation()}
                      color="primary"
                      disabled={!hasLabel}
                    />
                    <div className={classes.accordionSummaryContent}>
                      {label && (
                        <Typography variant="body2" className={classes.accordionSummaryLabel}>
                          {label}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        className={classes.accordionSummaryDescription}
                      >
                        {streamSource?.description || ""}
                      </Typography>
                      {!hasLabel && (
                        <Typography variant="caption" display="block" className={classes.accordionSummaryError}>
                          {intl.formatMessage(
                            { id: "roomTemplate.recordings.noLabel" },
                            { defaultMessage: "Label required" },
                          )}
                        </Typography>
                      )}
                    </div>
                    {recordingAdvancedSettingsAvailable && (
                      <div className={classes.accordionSummaryActions}>
                        <Typography variant="caption" color="textSecondary">
                          {intl.formatMessage({ id: "roomTemplate.recordings.autorun" }, { defaultMessage: "Autorun" })}
                        </Typography>
                        <Tooltip
                          title={
                            !isRecordingSelected
                              ? intl.formatMessage(
                                  { id: "roomTemplate.recordings.selectRecordingFirst" },
                                  { defaultMessage: "Select the recording first to enable this option" },
                                )
                              : ""
                          }
                        >
                          <span>
                            <Checkbox
                              checked={recording.autorun || false}
                              onChange={handleRecordingAutorunChange(streamSourceIndex)}
                              onClick={e => e.stopPropagation()}
                              color="primary"
                              disabled={!hasLabel || !isRecordingSelected}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </AccordionSummary>
                {recordingAdvancedSettingsAvailable && (
                  <AccordionDetails>
                    <div className={classes.accordionDetails}>
                      <div className={classes.formGrid}>
                        <Tooltip
                          title={
                            !isRecordingSelected
                              ? intl.formatMessage(
                                  { id: "roomTemplate.recordings.selectRecordingFirst" },
                                  { defaultMessage: "Select the recording first to enable this option" },
                                )
                              : ""
                          }
                        >
                          <div>
                            <Select
                              id={`recording-format-${streamSourceIndex}`}
                              name={`recording-format-${streamSourceIndex}`}
                              label={intl.formatMessage(
                                { id: "roomTemplate.recordings.format" },
                                { defaultMessage: "Format" },
                              )}
                              value={recording.format || "h264"}
                              onChange={handleRecordingFieldChange(streamSourceIndex, "format")}
                              elements={[
                                { value: "h264", label: "H264" },
                                { value: "h265", label: "H265" },
                                { value: "vp8", label: "VP8" },
                                { value: "vp9", label: "VP9" },
                              ]}
                              getValueFromElement={el => el.value}
                              getLabelFromElement={el => el.label}
                              disabled={!isRecordingSelected}
                            />
                          </div>
                        </Tooltip>
                        <Tooltip
                          title={
                            !isRecordingSelected
                              ? intl.formatMessage(
                                  { id: "roomTemplate.recordings.selectRecordingFirst" },
                                  { defaultMessage: "Select the recording first to enable this option" },
                                )
                              : ""
                          }
                        >
                          <div>
                            <Select
                              id={`recording-chunk-type-${streamSourceIndex}`}
                              name={`recording-chunk-type-${streamSourceIndex}`}
                              label={intl.formatMessage(
                                { id: "roomTemplate.recordings.chunkType" },
                                { defaultMessage: "Chunk Type" },
                              )}
                              value={recording.chunk_type || "ts"}
                              onChange={handleRecordingFieldChange(streamSourceIndex, "chunk_type")}
                              elements={[
                                { value: "ts", label: "TS" },
                                { value: "mp4", label: "MP4" },
                                { value: "mkv", label: "MKV" },
                              ]}
                              getValueFromElement={el => el.value}
                              getLabelFromElement={el => el.label}
                              disabled={!isRecordingSelected}
                            />
                          </div>
                        </Tooltip>
                        <Tooltip
                          title={
                            !isRecordingSelected
                              ? intl.formatMessage(
                                  { id: "roomTemplate.recordings.selectRecordingFirst" },
                                  { defaultMessage: "Select the recording first to enable this option" },
                                )
                              : ""
                          }
                        >
                          <div>
                            <Select
                              id={`recording-recorder-${streamSourceIndex}`}
                              name={`recording-recorder-${streamSourceIndex}`}
                              label={intl.formatMessage(
                                { id: "roomTemplate.recordings.recorder" },
                                { defaultMessage: "Recorder" },
                              )}
                              value={recording.recorder || "cloudfront"}
                              onChange={handleRecordingFieldChange(streamSourceIndex, "recorder")}
                              elements={[
                                { value: "cloudfront", label: "Cloudfront" },
                                { value: "s3", label: "S3" },
                                { value: "local", label: "Local" },
                              ]}
                              getValueFromElement={el => el.value}
                              getLabelFromElement={el => el.label}
                              disabled={!isRecordingSelected}
                            />
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  </AccordionDetails>
                )}
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="textSecondary">
            {intl.formatMessage(
              { id: "roomTemplate.recordings.noSources" },
              { defaultMessage: "No stream sources selected for recording" },
            )}
          </Typography>
        )}
        {/* Program Recording - Optional */}
        {(() => {
          const currentRecordings = values.file?.video_drains?.recordings || [];
          const programRecording = currentRecordings.find(rec => rec.src === "program");
          const isProgramSelected = !!programRecording;
          const programRecordingData = programRecording || {
            src: "program",
          };

          return (
            <Accordion key="recording-program" className={classes.programAccordion}>
              <AccordionSummary
                expandIcon={recordingAdvancedSettingsAvailable ? <ExpandMoreIcon /> : null}
                aria-controls="recording-program-content"
                id="recording-program-header"
              >
                <div className={classes.accordionSummaryContainer}>
                  <Checkbox
                    checked={isProgramSelected}
                    onChange={handleProgramRecordingSelectionChange}
                    onClick={e => e.stopPropagation()}
                    color="primary"
                  />
                  <Typography className={classes.programTitle}>program</Typography>
                  {recordingAdvancedSettingsAvailable && (
                    <div className={classes.accordionSummaryActions}>
                      <Typography variant="caption" color="textSecondary">
                        {intl.formatMessage({ id: "roomTemplate.recordings.autorun" }, { defaultMessage: "Autorun" })}
                      </Typography>
                      <Tooltip
                        title={
                          !isProgramSelected
                            ? intl.formatMessage(
                                { id: "roomTemplate.recordings.selectRecordingFirst" },
                                { defaultMessage: "Select the recording first to enable this option" },
                              )
                            : ""
                        }
                      >
                        <span>
                          <Checkbox
                            checked={programRecordingData.autorun || false}
                            onChange={handleProgramRecordingAutorunChange}
                            onClick={e => e.stopPropagation()}
                            color="primary"
                            disabled={!isProgramSelected}
                          />
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </AccordionSummary>
              {recordingAdvancedSettingsAvailable && (
                <AccordionDetails>
                  <div className={classes.accordionDetails}>
                    <div className={classes.formGrid}>
                      <Tooltip
                        title={
                          !isProgramSelected
                            ? intl.formatMessage(
                                { id: "roomTemplate.recordings.selectRecordingFirst" },
                                { defaultMessage: "Select the recording first to enable this option" },
                              )
                            : ""
                        }
                      >
                        <div>
                          <Select
                            id="recording-format-program"
                            name="recording-format-program"
                            label={intl.formatMessage(
                              { id: "roomTemplate.recordings.format" },
                              { defaultMessage: "Format" },
                            )}
                            value={programRecordingData.format || "h264"}
                            onChange={handleProgramRecordingFieldChange("format")}
                            disabled={!isProgramSelected}
                            elements={[
                              { value: "h264", label: "H264" },
                              { value: "h265", label: "H265" },
                              { value: "vp8", label: "VP8" },
                              { value: "vp9", label: "VP9" },
                            ]}
                            getValueFromElement={el => el.value}
                            getLabelFromElement={el => el.label}
                          />
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={
                          !isProgramSelected
                            ? intl.formatMessage(
                                { id: "roomTemplate.recordings.selectRecordingFirst" },
                                { defaultMessage: "Select the recording first to enable this option" },
                              )
                            : ""
                        }
                      >
                        <div>
                          <Select
                            id="recording-chunk-type-program"
                            name="recording-chunk-type-program"
                            label={intl.formatMessage(
                              { id: "roomTemplate.recordings.chunkType" },
                              { defaultMessage: "Chunk Type" },
                            )}
                            value={programRecordingData.chunk_type || "ts"}
                            onChange={handleProgramRecordingFieldChange("chunk_type")}
                            disabled={!isProgramSelected}
                            elements={[
                              { value: "ts", label: "TS" },
                              { value: "mp4", label: "MP4" },
                              { value: "mkv", label: "MKV" },
                            ]}
                            getValueFromElement={el => el.value}
                            getLabelFromElement={el => el.label}
                          />
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={
                          !isProgramSelected
                            ? intl.formatMessage(
                                { id: "roomTemplate.recordings.selectRecordingFirst" },
                                { defaultMessage: "Select the recording first to enable this option" },
                              )
                            : ""
                        }
                      >
                        <div>
                          <Select
                            id="recording-recorder-program"
                            name="recording-recorder-program"
                            label={intl.formatMessage(
                              { id: "roomTemplate.recordings.recorder" },
                              { defaultMessage: "Recorder" },
                            )}
                            value={programRecordingData.recorder || "cloudfront"}
                            onChange={handleProgramRecordingFieldChange("recorder")}
                            disabled={!isProgramSelected}
                            elements={[
                              { value: "cloudfront", label: "Cloudfront" },
                              { value: "s3", label: "S3" },
                              { value: "local", label: "Local" },
                            ]}
                            getValueFromElement={el => el.value}
                            getLabelFromElement={el => el.label}
                          />
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </AccordionDetails>
              )}
            </Accordion>
          );
        })()}
      </DataGridItem>
    </>
  );
};

RecordingsSection.propTypes = {
  intl: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  allStreamSources: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedStreamSources: PropTypes.array.isRequired,
  selectedRecordings: PropTypes.array.isRequired,
  handleRecordingSelectionChange: PropTypes.func.isRequired,
  handleRecordingAutorunChange: PropTypes.func.isRequired,
  handleRecordingFieldChange: PropTypes.func.isRequired,
  handleProgramRecordingSelectionChange: PropTypes.func.isRequired,
  handleProgramRecordingAutorunChange: PropTypes.func.isRequired,
  handleProgramRecordingFieldChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default injectIntl(withStyles(styles)(RecordingsSection));
